import BusinessIcon from '@mui/icons-material/Business';
import { Box, IconButton, Skeleton, TextField, Typography } from '@mui/material';
import { SwitchableTenantHierarchyDTO, TenantDTO } from 'probonio-shared-ui/api';
import { switchTenant, useTenant } from 'probonio-shared-ui/module/me';
import { mayReadAllTenants } from 'probonio-shared-ui/module/me/permissionSelectors';
import useDebounce from 'probonio-shared-ui/utils/useDebounce';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BasicDialog, DialogState, useNewDialogState } from '../../component/dialog';
import { DialogLoadingButton } from '../../component/dialog/DialogLoadingButton';
import TenantTree from '../../component/tenantHierarchy/TenantTree';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { resetEmployeeFilter, resetSorting, setTabIndex } from '../userManagement/filter';
import { SearchResultPagination } from './SearchResultPagination';
import { TENANT_SEARCH_PAGE_SIZE, useAdminTenantAccessData, useSwitchableTenantSearch, useTenantSearch } from './TenantHierarchyHooks';

interface Props {
  dialogState?: { dialogState: DialogState; handleOpen: () => void };
  showButton?: boolean;
}

const TENANT_TREE_MAX_HEIGHT = 65;

/**
 * Nests the admin search hits under each other by parentId. The list endpoint hands back a flat page and
 * resolving each hit's hierarchy meant one request per hit, so this is as much of the tree as the single
 * search request can reconstruct: a hit whose parent did not match stays a root, because nothing in the
 * response says where it hangs.
 */
function buildAdminSearchTree(hits: TenantDTO[]): SwitchableTenantHierarchyDTO[] {
  const nodes: { hit: TenantDTO; node: SwitchableTenantHierarchyDTO }[] = hits.map(hit => ({
    hit,
    node: { tenant: hit, children: [] },
  }));
  const nodesById = new Map(nodes.map(({ hit, node }): [string, SwitchableTenantHierarchyDTO] => [hit.id, node]));

  const roots: SwitchableTenantHierarchyDTO[] = [];
  for (const { hit, node } of nodes) {
    const parent = hit.parentId ? nodesById.get(hit.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export const TenantSwitcher: React.FC<Props> = ({ dialogState, showButton = true }) => {
  const { tenant, isLoading } = useTenant();
  const { user, adminTenantAccessData: initialAdminTenantAccessData } = useAppSelector(state => state.me);
  const canReadAll = useAppSelector(state => mayReadAllTenants(user));
  const dispatch = useAppDispatch();
  const { t } = useTranslation('navigationModule');
  const internalDialogState = useNewDialogState();
  const selectTenantDialog = dialogState ?? internalDialogState;

  const [inputValue, setInputValue] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState(tenant?.id);
  const [currentPage, setCurrentPage] = useState(0);
  const debouncedInput = useDebounce(inputValue, 500).trim();
  const { data: adminTenantAccessData } = useAdminTenantAccessData(user, initialAdminTenantAccessData);
  const {
    data: searchedTenants,
    isLoading: isTenantSearchLoading,
    isFetching: isTenantSearchFetching,
    isError: isTenantSearchError,
  } = useTenantSearch(debouncedInput, currentPage, canReadAll);
  const {
    data: switchableSearchTenants,
    isLoading: isSwitchableSearchLoading,
    isError: isSwitchableSearchError,
  } = useSwitchableTenantSearch(user, debouncedInput, !canReadAll);
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedInput]);

  const switchableTenants = adminTenantAccessData?.switchableTenants ?? [];
  // Outside search only the roots of the forest are surfaced - everything below them renders as their children.
  const rootTenantIds = switchableTenants.map(node => node.tenant.id);

  useEffect(() => {
    if (!selectTenantDialog.dialogState.isOpen) {
      return;
    }
    setInputValue('');
    setSelectedTenantId(tenant?.id);
  }, [selectTenantDialog.dialogState.isOpen, tenant?.id]);

  const handleChangeTenant = useCallback(() => {
    if (!selectedTenantId) return;
    // Close explicitly instead of relying on the switch unmounting us: the dialog only ever disappeared because
    // the guards swap the whole page - navbar included - for a spinner while the new tenant is still loading.
    // With the tenant already cached that never happens and the dialog stayed open over the switched tenant.
    selectTenantDialog.dialogState.handleClose();
    navigate('/');
    dispatch(switchTenant({ tenantId: selectedTenantId, isAdmin: true }));
    dispatch(resetSorting());
    dispatch(resetEmployeeFilter());
    dispatch(setTabIndex(0));
  }, [dispatch, navigate, selectTenantDialog.dialogState, selectedTenantId]);

  if (isLoading) {
    return <Skeleton />;
  }

  if (!tenant) {
    return null;
  }

  const isSearchMode = debouncedInput.length > 0;
  const searchHits = searchedTenants?.tenants;
  const totalTenantSearchHits = searchedTenants?.totalCount ?? 0;

  // Two search sources, because only global admins may call the tenant list endpoint: everyone else searches the
  // tenants /tenants/hierarchies hands them, refetched per search term.
  // Admin hits come as a flat page from the list endpoint and are nested by parentId, so a matching parent and its
  // matching children render as one tree instead of as siblings.
  // Everything outside search is already covered by the hierarchies response, so nothing is fetched for it here.
  const adminSearchTree = buildAdminSearchTree(searchHits ?? []);
  const searchTree = canReadAll ? adminSearchTree : (switchableSearchTenants?.switchableTenants ?? []);
  const allSwitchableSearchTenantIds = switchableSearchTenants?.tenantIds ?? [];
  const searchTenantIds = canReadAll
    ? (searchHits?.map(hit => hit.id) ?? [])
    : allSwitchableSearchTenantIds.slice(currentPage * TENANT_SEARCH_PAGE_SIZE, (currentPage + 1) * TENANT_SEARCH_PAGE_SIZE);
  const isSearchLoading = canReadAll ? isTenantSearchLoading : isSwitchableSearchLoading;
  const isSearchError = canReadAll ? isTenantSearchError : isSwitchableSearchError;

  const visibleTenantIds = isSearchMode ? searchTenantIds : rootTenantIds;
  const tree = isSearchMode ? searchTree : switchableTenants;

  const totalSearchHits = canReadAll ? totalTenantSearchHits : allSwitchableSearchTenantIds.length;
  const pageCount = Math.max(1, Math.ceil(totalSearchHits / TENANT_SEARCH_PAGE_SIZE));
  const hasPrevPage = currentPage > 0;
  const hasNextPage = currentPage + 1 < pageCount;
  const isSearchFetching = canReadAll && isTenantSearchFetching;
  const showSearchResultInfo = isSearchMode && !isSearchLoading && !isSearchError && searchTenantIds.length > 0 && pageCount > 1;
  const searchResultRangeFrom = totalSearchHits === 0 ? 0 : currentPage * TENANT_SEARCH_PAGE_SIZE + 1;
  const searchResultRangeTo = currentPage * TENANT_SEARCH_PAGE_SIZE + searchTenantIds.length;

  return (
    <>
      {showButton && (
        <IconButton sx={{ color: 'text.secondary' }} title={t('changeTenantDialog.title')} onClick={selectTenantDialog.handleOpen}>
          <BusinessIcon />
        </IconButton>
      )}
      <BasicDialog
        dialogState={selectTenantDialog.dialogState}
        title={t('changeTenantDialog.title')}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { overflow: 'visible' } }}
        content={
          selectTenantDialog.dialogState.isOpen ? (
            <Box>
              <TextField
                size="small"
                placeholder={t('changeTenantDialog.searchPlaceholder')}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                fullWidth
                autoFocus
                sx={{ marginTop: 0.1, marginBottom: 1 }}
              />
              <Typography
                sx={{
                  mb: 2,
                  color: 'text.secondary',
                }}
              >
                {t('changeTenantDialog.text')}
              </Typography>
              <Box sx={{ maxHeight: theme => `min(${theme.spacing(TENANT_TREE_MAX_HEIGHT)}, 70vh)`, overflowY: 'auto' }}>
                <TenantTree
                  tenantIds={visibleTenantIds}
                  currentTenantId={tenant.id}
                  search={debouncedInput}
                  isSearchLoading={isSearchLoading}
                  isSearchError={isSearchError}
                  rootTenants={tree}
                  selectedTenantId={selectedTenantId}
                  setSelectedTenantId={setSelectedTenantId}
                />
              </Box>
            </Box>
          ) : null
        }
        actions={
          // DialogActions only pads 8px, so the hit count would sit left of everything in the content above it,
          // which MUI pads by 24px. The extra 16px line it up with the search field and the dialog text.
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', paddingLeft: '16px' }}>
            {showSearchResultInfo && (
              <SearchResultPagination
                from={searchResultRangeFrom}
                to={searchResultRangeTo}
                total={totalSearchHits}
                hasPrevPage={hasPrevPage}
                hasNextPage={hasNextPage}
                disabled={isSearchFetching}
                onPrevPage={() => setCurrentPage(page => page - 1)}
                onNextPage={() => setCurrentPage(page => page + 1)}
              />
            )}
            <DialogLoadingButton
              loading={isLoading}
              onClick={handleChangeTenant}
              variant="contained"
              disabled={!selectedTenantId || selectedTenantId === tenant?.id}
              sx={{ marginLeft: 'auto' }}
            >
              {t('changeTenantDialog.submit')}
            </DialogLoadingButton>
          </Box>
        }
      />
    </>
  );
};
